import { get, isEmpty } from 'lodash';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import moment from 'moment';
import { useAtom, useSetAtom } from 'jotai';
import { setSlice } from '@store/trends/birdChart/actions';
import { initMetricFilter, removeMetricFilter } from '@store/trends/base/actions';
import { metricFilterLookupSelector } from '@store/trends/base/selectors';
import {
  appMetricFilteringSelector,
  filterSelector,
  getUserProfile,
  intervalSelector,
  realTimeModeOnTrendsSelector,
} from '@store/common/selectors';
import { getDistributionMetrics } from '@components/metrics-panel-hz/utils/metricsMapping';
import { isAppModule } from '@utils/constants';
import { dimensionByTypesSelector } from '@store/common/dimensionList/selectors';
import {
  findDimensionDetailsInFilter,
  getFilterModeFromDimensionTypes,
} from '@src/utils/filter/getDimensionTypesFromFilter';
import { DIMENSION_TYPES_CONSTANTS, getMetricMetaByKey } from '@utils/metrics';
import { Tabs } from '@store/trends/base/reducer';
import { sliceAreaAtom } from '../atoms';
import { CHART_TYPE } from './utils';
import { METRIC_FILTER_TYPES, buildMetricFilter } from '../utils/metricFilterUtils';
import { tableCompareIntervalSelector } from '@store/trends/birdChart/selectors';
import { getValidPresetKeys } from '@src/utils/presets/utils';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { isPercentileMetric } from '@src/utils/percentile';
import { metricsStore } from '@globalStore/metricsStore';
import { metricKeys } from '@pulse/metrics';
import {
  isTableCompareEnabledAtom,
  activeTabAtom,
} from '@src/components/dimension-panel-hz/atoms/index';
import { getAllowedGranularity } from '@src/components/common/GranularitySelect/utils';
import { GRANULARITY } from '@src/utils/granularity';
const { PT1H } = GRANULARITY;
const getMarkArea = {
  timeseries: (area) => {
    if (!isEmpty(area)) {
      return {
        data: [
          [{ xAxis: moment(area[0]).toISOString() }, { xAxis: moment(area[1]).toISOString() }],
        ],
      };
    }
    return [];
  },
  distribution: (area) => {
    if (!isEmpty(area)) {
      return {
        data: [[{ xAxis: area[0] }, { xAxis: area[1] }]],
      };
    }
    return [];
  },
};

// Convert granularity to milliseconds using GRANULARITY constants
const getGranularityInMs = (granularity: string): number => {
  const granularityToMs: Record<string, number> = {
    [GRANULARITY.PT10S]: 10 * 1000,
    [GRANULARITY.PT1M]: 60 * 1000,
    [GRANULARITY.PT5M]: 5 * 60 * 1000,
    [GRANULARITY.PT15M]: 15 * 60 * 1000,
    [GRANULARITY.PT30M]: 30 * 60 * 1000,
    [GRANULARITY.PT1H]: 60 * 60 * 1000,
    [GRANULARITY.PT6H]: 6 * 60 * 60 * 1000,
    [GRANULARITY.PT12H]: 12 * 60 * 60 * 1000,
    [GRANULARITY.P1D]: 24 * 60 * 60 * 1000,
    [GRANULARITY.P1W]: 7 * 24 * 60 * 60 * 1000,
  };
  return granularityToMs[granularity] || 60 * 1000; // Default to 1 minute
};

// Get granularity unit for moment operations
const getGranularityUnit = (granularity: string): moment.unitOfTime.StartOf => {
  const granularityUnits: Record<string, moment.unitOfTime.StartOf> = {
    [GRANULARITY.PT10S]: 'second',
    [GRANULARITY.PT1M]: 'minute',
    [GRANULARITY.PT5M]: 'minute',
    [GRANULARITY.PT15M]: 'minute',
    [GRANULARITY.PT30M]: 'minute',
    [GRANULARITY.PT1H]: 'hour',
    [GRANULARITY.PT6H]: 'hour',
    [GRANULARITY.PT12H]: 'hour',
    [GRANULARITY.P1D]: 'day',
    [GRANULARITY.P1W]: 'week',
  };
  return granularityUnits[granularity] || 'minute';
};

function useSelect({ instance, chartMode, enabled, metric }) {
  const dispatch = useDispatch();
  const metricFilterLookup = useSelector(metricFilterLookupSelector);
  const { metricsMeta } = metricsStore.getState();
  const liveMode = useSelector(realTimeModeOnTrendsSelector);
  const appMetricFilteringFeature = useSelector(appMetricFilteringSelector);
  const globalFilter = useSelector(filterSelector);
  const dimensionByTypes = useSelector(dimensionByTypesSelector);
  const distributionMetrics = getDistributionMetrics();
  const { usecaseKey }: any = useSelector(activeDashboardSelector);
  const userProfile = useSelector(getUserProfile);
  const [isTableCompareEnabled, tableCompareToggle] = useAtom(isTableCompareEnabledAtom);
  const tableCompareInterval: any = useSelector(tableCompareIntervalSelector);
  const interval = useSelector(intervalSelector);
  const [sliceArea, setSliceArea] = useAtom(sliceAreaAtom);

  const setDimensionPanelTab = useSetAtom(activeTabAtom);

  const addMarkAreaForTableCompareInterval = (tableCompareInterval) => {
    const itemStyle = {
      color: 'rgba(60, 60, 60, 0.2)',
    };

    tableCompareInterval[0].itemStyle = itemStyle;
    return tableCompareInterval;
  };

  const handleTableInterval = useCallback(
    (area) => {
      if (instance) {
        try {
          const { series, _viewType = '' } = instance.getOption();
          if (_viewType in getMarkArea) {
            const compareArea = getMarkArea[_viewType](area);
            const compareMarkArea = addMarkAreaForTableCompareInterval(compareArea.data[0]);
            series[0].markArea.data[1] = compareMarkArea;
            instance.setOption({ series }, { replaceMerge: 'series' });
          }
        } catch (err) {
          // ignore
        }
      }
    },
    [instance],
  );

  // handle chart slice action
  const handleSelect = useCallback(
    (area) => {
      if (instance) {
        try {
          const { series, _viewType = '' } = instance.getOption();
          if (_viewType in getMarkArea) {
            const markArea = getMarkArea[_viewType](area);
            series[0].markArea = markArea;
            instance.setOption({ series }, { replaceMerge: 'series' });
          }
        } catch (err) {
          // ignore
        }
      }
    },
    [instance],
  );

  // Check and modify selected range to align with minimum granularity
  const modifyRange = (range: [number, number] | null): [number, number] | null => {
    if (!range) return null;
    const [start, end] = range;

    const allowedGranularity = getAllowedGranularity(interval);
    const minimumGranularity = allowedGranularity[0]; // First element is minimum granularity

    const minRangeMs = getGranularityInMs(minimumGranularity);
    const currentRangeMs = end - start;
    const granularityUnit = getGranularityUnit(minimumGranularity);

    let adjustedStart = start;
    let adjustedEnd = end;

    // Use timezone from userProfile; for IST, snap to local boundaries to align with data buckets
    // For UTC and other timezones, snap to UTC boundaries since backend expects UTC-aligned timestamps
    const userTimezone = get(userProfile, 'tzCode');
    const isISTTimezone = userTimezone === 'IST';
    const boundaryMode = isISTTimezone ? 'local' : 'utc';

    // Always snap to boundaries for consistency with data granularity
    if (currentRangeMs <= minRangeMs) {
      // example if PT1H is minimum granularity and sliced range is 1:15 to 2:00 it should snap to 1:00 to 2:00
      // Range is at or below minimum - snap to single granularity unit
      adjustedStart = moment(start)[boundaryMode]().startOf(granularityUnit).valueOf();
      adjustedEnd = moment(start)
        [boundaryMode]()
        .startOf(granularityUnit)
        .add(1, granularityUnit)
        .valueOf();
    } else {
      // example if PT1H is minimum granularity and sliced range is 1:15 to 2:30 it should snap to 1:00 to 3:00
      // Range exceeds minimum - snap to nearest left and right boundaries
      adjustedStart = moment(start)[boundaryMode]().startOf(granularityUnit).valueOf();
      adjustedEnd = moment(end)
        [boundaryMode]()
        .startOf(granularityUnit)
        .add(1, granularityUnit)
        .valueOf();
    }

    return [adjustedStart, adjustedEnd];
  };

  const handleBrushEnd = useCallback(
    (brushData) => {
      const option = instance.getOption();
      const metric = option._chartMetric;

      const area = get(brushData, ['areas'], []);
      const range = get(area, ['0', 'coordRange'], null);

      if (option._viewType === 'timeseries') {
        const adjustedRange: any = modifyRange(range);
        setSliceArea(adjustedRange);
        // time slice on dimension table
        // set or clear range
        dispatch(setSlice([0, 100], adjustedRange || [0, 0]));
      } else if (option._viewType === 'distribution') {
        const distributionConfig = option._distributionConfig;

        // Feature flag for app metric filtering
        if (isAppModule) {
          if (appMetricFilteringFeature) {
            const { dimensionTypes } = findDimensionDetailsInFilter(globalFilter, dimensionByTypes);
            const filterMode = getFilterModeFromDimensionTypes(dimensionTypes);
            const PRESET_KEYS = getValidPresetKeys();
            /** PUI-1649: Support secondary filter of AvgNetworkRequestDuration on events scope */
            if (usecaseKey !== PRESET_KEYS.APP_NETWORK_PERFORMANCE) {
              /** PUI-1666: [FE] Support secondary filter of AvgNetworkRequestDuration in any scope trends */
              if (
                filterMode === DIMENSION_TYPES_CONSTANTS.eventTagKeyValueMetadata &&
                metric !== metricKeys?.AverageRequestDuration
              ) {
                message.warning(
                  'Secondary filter is not supported when global filter has dimension with type eventTagKeyValueMetadata',
                );
                return;
              }
            }
            if (isPercentileMetric(getMetricMetaByKey({ metricKey: metric }))) {
              message.warning('SF is support for average only');
              return;
            }
            /** HP-4838: open Network Request tab when secondary filter by AverageRequestDuration */
            if (metric === metricKeys?.AverageRequestDuration) {
              setDimensionPanelTab(Tabs.AppAnalyseNetworkRequest);
            }
          } else {
            return;
          }
        }

        if (liveMode) {
          message.warning('Secondary filtering is not supported while on real time mode');
          return;
        }

        if (isTableCompareEnabled) {
          tableCompareToggle(false);
        }

        if (!Object.keys(distributionMetrics).includes(metric)) {
          message.warning('Secondary filtering is not supported by the selected metric');
          return;
        }

        // update metric filter
        const data = get(option, ['series', 0, 'data'], null);

        if (data && range) {
          const metricFilter = buildMetricFilter({ metric, data, range, distributionConfig });
          if (metricFilter) {
            dispatch(initMetricFilter(metricFilter));
          } else {
            message.warning('Invalid selection. Cannot apply a metric filter.');
            return;
          }
        } else {
          // removing bound type metric filter
          dispatch(removeMetricFilter({ metric, meta: { type: METRIC_FILTER_TYPES.bound } }));
        }
      }

      // clear brush selection
      instance.dispatchAction({
        type: 'brush',
        command: 'clear',
        areas: [],
      });
    },
    [
      appMetricFilteringFeature,
      dimensionByTypes,
      dispatch,
      globalFilter,
      instance,
      liveMode,
      metricsMeta,
      usecaseKey,
      distributionMetrics,
      setSliceArea,
      isTableCompareEnabled,
      modifyRange,
    ],
  );

  // register chart events
  useEffect(() => {
    if (instance && enabled) {
      instance.on('brushEnd', handleBrushEnd);
      return () => {
        instance.off('brushEnd', handleBrushEnd);
      };
    }
  }, [instance, enabled, handleBrushEnd]);

  // persist slice for timeseries
  useEffect(() => {
    if (sliceArea && [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode)) {
      handleSelect(sliceArea);
    }
  }, [instance, chartMode, handleSelect, sliceArea]);

  // persist slice for timeseries
  useEffect(() => {
    if (sliceArea && [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode)) {
      if (isTableCompareEnabled && tableCompareInterval) {
        handleTableInterval([tableCompareInterval.from, tableCompareInterval.to]);
      } else {
        handleSelect(sliceArea);
      }
    }
  }, [instance, chartMode, handleSelect, sliceArea, isTableCompareEnabled, tableCompareInterval]);

  // persist slice for metric filter
  useEffect(() => {
    if ([CHART_TYPE.DISTRIBUTION].includes(chartMode)) {
      const range = get(metricFilterLookup, [metric, 'meta', 'range'], []);
      handleSelect(range);
    }
  }, [instance, chartMode, metric, metricFilterLookup]);
}

export default useSelect;
